<template>
  <SrpModal v-model:isVisible="isVisible" :isClosable="false">
    <template #content>
      <h1 class="global-h1" style="margin-bottom: 20px">Edit Package</h1>

      <!-- Package edit form -->
      <div class="package-edit-form">
        <div class="package-edit-form__title-and-input">
          <h5 class="global-h5 package-edit-form__title">Title</h5>
          <input
            :class="{
              'global-text-input': true,
              'global-text-input--error': !isFormValid && !packageToEdit.name,
              'global-text-input--big': true,
              'package-edit-form__input': true,
            }"
            @focus="isFormValid = true"
            type="text"
            v-model="packageToEdit.name"
            ref="domRefNameTextInput"
          />
        </div>

        <div class="package-edit-form__title-and-input">
          <h5 class="global-h5 package-edit-form__title">Price <span style="color: rgba(0, 0, 0, 0.5); font-size: 16px; font-weight: 600">(starts at)</span></h5>
          <input class="global-text-input package-edit-form__input" type="text" style="max-width: 150px" v-model="packageToEdit.price" inputmode="numeric" />
        </div>

        <div class="package-edit-form__title-and-input">
          <h5 class="global-h5 package-edit-form__title">Description</h5>
          <textarea
            :class="{
              'global-textarea': true,
              'global-textarea--error': !isFormValid && !packageToEdit.description,
              'package-edit-form__input': true,
            }"
            @focus="isFormValid = true"
            style="height: 90px; min-height: 90px"
            v-model="packageToEdit.description"
          ></textarea>
        </div>

        <div class="package-edit-form__title-and-input">
          <h5 class="global-h5 package-edit-form__title">Deliverables</h5>

          <div class="package-edit-form__input-and-delete-button" v-for="(deliverable, index) in packageToEdit.deliverables" :key="index" style="margin-bottom: 10px">
            <input class="global-text-input package-edit-form__input" type="text" v-model="packageToEdit.deliverables[index]" ref="domRefDeliverableTextInput" />

            <LinkWithIcon class="package-edit-form__delete-button" color="red" @click="packageToEdit.deliverables.splice(index, 1)">
              <template #icon><IconEmbedded name="trashcan-alt_2" :size="21" /></template>
            </LinkWithIcon>
          </div>

          <LinkWithIcon class="package-edit-form__add-deliverable" isDottedUnderline color="blue" @click="addDeliverable">
            <template #icon><IconEmbedded name="plus_2-5" :size="20" /></template>
            <span>Add deliverable</span>
          </LinkWithIcon>
        </div>
      </div>
      <!-- / Package edit form -->
    </template>

    <template #footer>
      <SrpButton :isDisabled="isSaving" fill="outlined" style="margin-right: 12px" @click="isVisible = false">Cancel</SrpButton>
      <SrpButton
        :isDisabled="isSaving"
        @click="
          async () => {
            await validateAndSave();
            if (isFormValid) {
              emit('save');
              isVisible = false;
            }
          }
        "
      >
        <template v-if="isSaving" #icon><LoadingIcon /></template>
        <template v-else #icon><IconEmbedded name="save_2" :size="20" /></template>
        Save
      </SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { ref, nextTick, computed, watch } from "vue";
import lodashCloneDeep from "lodash-es/cloneDeep";
import CreatorPackagesRepo from "@repos/CreatorPackagesRepo";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";

// Types
import { CreatorPackage, CreatorPackagePayload } from "@contracts/creatorPackages";

// DOM refs
const domRefDeliverableTextInput = ref<any>(null);
const domRefNameTextInput = ref<any>(null);

const emit = defineEmits<{
  (e: "save"): void;
}>();

// Toggle visibility ==========================================================
const creatorId = ref<string | null>(null);
const packageToEdit = ref<Partial<CreatorPackage> | null>(null);

const isVisible = ref<boolean>(false);

async function openModal(_creatorId: string, _packageToEdit?: Partial<CreatorPackage>) {
  creatorId.value = _creatorId;

  if (_packageToEdit) {
    packageToEdit.value = lodashCloneDeep(_packageToEdit);
  } else {
    packageToEdit.value = {
      name: "",
      price: 0,
      description: "",
      deliverables: [""],
    };
  }
  isVisible.value = true;

  await nextTick();
  if (!packageToEdit.value.id) {
    domRefNameTextInput.value?.focus();
  }
}

// Validate and save ==========================================================
const isSaving = ref<boolean>(false);
const isFormValid = ref<boolean>(true);

async function validateAndSave() {
  if (!packageToEdit.value.name || !packageToEdit.value.description) {
    isFormValid.value = false;
    return;
  }
  const creatorPackagePayload: CreatorPackagePayload = {
    name: packageToEdit.value.name,
    price: packageToEdit.value.price,
    description: packageToEdit.value.description,
    deliverables: packageToEdit.value.deliverables.filter(d => Boolean(d)),
  };

  isSaving.value = true;
  if (packageToEdit.value.id) {
    await CreatorPackagesRepo.updatePackage(creatorId.value, packageToEdit.value.id, creatorPackagePayload);
  } else {
    await CreatorPackagesRepo.createPackage(creatorId.value, creatorPackagePayload);
  }
  isSaving.value = false;
}

// Add deliverable ============================================================
async function addDeliverable() {
  packageToEdit.value.deliverables.push("");
  await nextTick();
  domRefDeliverableTextInput.value.at(-1)?.focus();
}

// Cast the price value to int ================================================
watch(
  () => packageToEdit.value?.price,
  () => {
    packageToEdit.value.price = Number(`${packageToEdit.value.price}`.replace(/\D/gim, ""));
  }
);

// Define expose ==============================================================
defineExpose({
  openModal,
});
</script>

<style scoped lang="scss">
// Package edit form ==========================================================
.package-edit-form {
  gap: 20px;
  display: flex;
  flex-direction: column;

  &__title-and-input {
  }

  &__title {
    margin-bottom: 5px;
    font-weight: 600;
  }

  &__input {
  }

  &__input-and-delete-button {
    gap: 0 10px;
    display: flex;
    align-items: center;
  }

  &__delete-button {
  }

  &__add-deliverable {
  }
}
</style>
